import * as _ from 'lodash';

import * as Axon from '@jlekie/axon';
import * as Wellspring from '@greenheck/wellspring';

import * as ConfigurationModels from './services/configuration.model';
import * as ConfigurationModelEntity from './services/configuration.modelEntity';
import * as ConfigurationModelLayout from './services/configuration.modelLayout';
import * as ConfigurationModelGrid from './services/configuration.modelGrid';
import * as ConfigurationModelDictionary from './services/configuration.modelDictionary';
import * as ConfigurationTable from './services/configuration.table';
import * as ConfigurationCore from './services/configuration.core';
import * as ConfigurationLayout from './services/configuration.layout';
import * as ConfigurationProjection from './services/configuration.projection';
import * as ConfigurationProjectionLayout from './services/configuration.projectionLayout';
import * as ConfigurationProjectionIntegration from './services/configuration.projectionIntegration';
import * as ConfigurationProjectionLayoutGrid from './services/configuration.projectionLayoutGrid';

import * as LouverSelection from './services/louverSelection.selection';

export type DeferredType<T extends (...args: any[]) => any> = ReturnType<T> extends Promise<any> ? T : (...args: Parameters<T>) => Promise<ReturnType<T>>;

export interface CatalogEntry {
    key: string;
    name: string;
    displayName: string;
}

export interface ServiceClientMethods {
    getCatalogEntities(): Promise<CatalogEntry[]>;
    getCatalogLayouts(): Promise<CatalogEntry[]>;
    getCatalogContracts(): Promise<CatalogEntry[]>;
    getCatalogTables(): Promise<CatalogEntry[]>;

    getEntity(name: string): Promise<ConfigurationModelEntity.Entity>;
    getLayout(name: string): Promise<ConfigurationModelLayout.Layout>;
    getContract(name: string): Promise<unknown>;
    getGrid(name: string): Promise<ConfigurationModelGrid.Grid>;
    getDisplayDictionary(name: string): Promise<ConfigurationModelDictionary.DisplayDictionary>;
    getTable(name: string): Promise<ConfigurationTable.Table>;

    createCoreEntity(entityModel: ConfigurationModelEntity.Entity, defaults?: string[]): Promise<ConfigurationCore.Entity>;
    createLayoutEntity(coreEntity: ConfigurationCore.Entity, layoutModel?: ConfigurationModelLayout.Layout): Promise<ConfigurationProjectionLayout.Entity>;
    createLayoutScenario(layoutEntity: ConfigurationProjectionLayout.Entity, scenario: ConfigurationCore.ConflictScenario): Promise<ConfigurationProjection.ConflictScenarioLayout>;

    updateEntity(entity: ConfigurationCore.Entity, state?: Record<string, string>): Promise<ConfigurationCore.UpdateResult>;
    snapshotEntityState(entity: ConfigurationCore.Entity): Promise<void>;
    restoreEntityState(entity: ConfigurationCore.Entity, deltaIndex: number): Promise<ConfigurationCore.UpdateResult>;

    createLouverSelectorCoreEntity(): Promise<ConfigurationCore.Entity>;
    selectLouvers(selectorEntity: ConfigurationCore.Entity): Promise<ConfigurationCore.Entity[]>;

    addChild(entity: ConfigurationCore.Entity, childName: string, louverSizeIntegrationEntity?: ConfigurationProjectionIntegration.Entity): Promise<ConfigurationCore.UpdateResult>;
    applyIntegration(entity: ConfigurationCore.Entity, childName: string, integrationEntity: ConfigurationProjectionIntegration.Entity): Promise<ConfigurationCore.UpdateResult>;

    createIntegrationProjection(entity: ConfigurationCore.Entity): Promise<ConfigurationProjectionIntegration.Entity>;

    createLayoutGrid(entity: ConfigurationCore.Entity | ConfigurationCore.Entity[], gridModel: ConfigurationModelGrid.Grid): Promise<ConfigurationProjectionLayoutGrid.Grid>;

    loadTestGrid(): Promise<ConfigurationModelGrid.Grid>;
}
export type DeferredServiceClientMethods = {
    [P in keyof ServiceClientMethods]: DeferredType<ServiceClientMethods[P]>
}

export class ServiceClient implements ServiceClientMethods {
    public constructor(
        public url: string,
        public catalogClient: ConfigurationModels.CatalogServiceClient,
        public dataAccessClient: ConfigurationModels.DataAccessServiceClient,
        public coreProviderClient: ConfigurationCore.ProviderServiceClient,
        public coreLifecycleClient: ConfigurationCore.LifecycleServiceClient,
        public layoutProviderClient: ConfigurationLayout.ProviderServiceClient,
        public louverSelectionClient: LouverSelection.ConfigurationServiceClient,
        public projectionProviderClient: ConfigurationProjection.ProviderServiceClient
    ) {}

    public async getCatalogEntities() {
        const entities = await this.catalogClient.getEntities();

        return entities.map(e => ({
            key: e.name.replace('/', '-'),
            name: e.name,
            displayName: e.displayName
        }));
    }
    public async getCatalogLayouts() {
        const layouts = await this.catalogClient.getLayouts();

        return layouts.map(e => ({
            key: e.name.replace('/', '-'),
            name: e.name,
            displayName: e.displayName
        }));
    }
    public async getCatalogContracts() {
        const contracts = await this.catalogClient.getContracts();

        return contracts.map(e => ({
            key: e.name.replace('/', '-'),
            name: e.name,
            displayName: e.displayName
        }));
    }
    public async getCatalogTables() {
        const tables = await this.catalogClient.getTables();

        return tables.map(e => ({
            key: e.name.replace('/', '-'),
            name: e.name,
            displayName: e.displayName
        }));
    }

    public async getEntity(name: string) {
        return this.dataAccessClient.loadEntity(name);
    }
    public async getLayout(name: string) {
        return this.dataAccessClient.loadLayout(name);
    }
    public async getContract(name: string) {
        return this.dataAccessClient.loadContract(name);
    }
    public async getGrid(name: string): Promise<ConfigurationModelGrid.Grid> {
        return this.dataAccessClient.loadGrid(name);
    }
    public async getDisplayDictionary(name: string): Promise<ConfigurationModelDictionary.DisplayDictionary> {
        return this.dataAccessClient.loadDisplayDictionary(name);
    }
    public async getTable(name: string): Promise<ConfigurationTable.Table> {
        return this.dataAccessClient.loadTable(name);
    }

    public async createCoreEntity(entityModel: ConfigurationModelEntity.Entity, defaults?: string[]) {
        const coreEntity = await (defaults ? this.coreProviderClient.createEntity(entityModel, defaults) : this.coreProviderClient.createEntity(entityModel));

        return coreEntity;
    }
    public async createLayoutEntity(coreEntity: ConfigurationCore.Entity, layoutModel?: ConfigurationModelLayout.Layout) {
        // const layoutEntity = await this.layoutProviderClient.createEntity(coreEntity, layoutModel);
        const layoutEntity = layoutModel ? await this.projectionProviderClient.createLayout(coreEntity, layoutModel) : await this.projectionProviderClient.createLayout(coreEntity);

        // const layoutEntity = new ConfigurationLayout.Entity();
        // layoutEntity.groups = [
        //     new ConfigurationLayout.Group({
        //         name: 'Test',
        //         displayKey: 'Test'
        //     })
        // ]

        return layoutEntity;
    }
    public async updateEntity(entity: ConfigurationCore.Entity, state?: Record<string, string>) {
        if (state)
            return this.coreLifecycleClient.update(entity, _.map(state, (value, key) => `${key}=${value}`));
        else
            return this.coreLifecycleClient.sanitize(entity);
    }
    public async snapshotEntityState(entity: ConfigurationCore.Entity): Promise<void> {
        await this.coreLifecycleClient.snapshotState(entity);
    }
    public async restoreEntityState(entity: ConfigurationCore.Entity, deltaIndex: number): Promise<ConfigurationCore.UpdateResult> {
        return this.coreLifecycleClient.restoreState(entity, deltaIndex);
    }

    public async createLouverSelectorCoreEntity() {
        return this.louverSelectionClient.createSelectorEntity();
    }
    public async selectLouvers(selectorEntity: ConfigurationCore.Entity) {
        return this.louverSelectionClient.selectLouverSizes(selectorEntity);
    }

    public async addChild(entity: ConfigurationCore.Entity, childName: string, integrationEntity?: ConfigurationProjectionIntegration.Entity) {
        if (integrationEntity)
            return this.coreLifecycleClient.addChild(entity, childName, integrationEntity, '');
        else
            return this.coreLifecycleClient.addChild(entity, childName);
    }
    public async applyIntegration(entity: ConfigurationCore.Entity, childName: string, integrationEntity: ConfigurationProjectionIntegration.Entity) {
        return this.coreLifecycleClient.applyIntegration(entity, integrationEntity, childName);
    }

    public async createIntegrationProjection(entity: ConfigurationCore.Entity): Promise<ConfigurationProjectionIntegration.Entity> {
        return this.projectionProviderClient.createIntegration(entity);
    }

    public async createLayoutGrid(entity: ConfigurationCore.Entity | ConfigurationCore.Entity[], gridModel: ConfigurationModelGrid.Grid) {
        if (_.isArray(entity))
            return this.projectionProviderClient.createLayoutGrid(entity, gridModel);
        else
            return this.projectionProviderClient.createLayoutGrid(entity, gridModel);
    }
    public async loadTestGrid(): Promise<ConfigurationModelGrid.Grid> {
        return new ConfigurationModelGrid.Grid({
            childSelector: '',
            columns: [
                new ConfigurationModelGrid.Column({
                    name: 'id',
                    displayKey: 'ID',
                    source: 'id'
                }),
                new ConfigurationModelGrid.Column({
                    name: 'width',
                    displayKey: 'Width',
                    source: 'Width'
                }),
                new ConfigurationModelGrid.Column({
                    name: 'height',
                    displayKey: 'Height',
                    source: 'Height'
                })
            ]
        });
    }

    public async createLayoutScenario(layoutEntity: ConfigurationProjectionLayout.Entity, scenario: ConfigurationCore.ConflictScenario) {
        return this.projectionProviderClient.createLayoutConflictScenario(layoutEntity, scenario);
    }
}

export const serviceClientMethodKeys = Object.getOwnPropertyNames(ServiceClient.prototype);
export function createDeferredServiceClient(deferredServiceClient: ServiceClient | Promise<ServiceClient>): DeferredServiceClientMethods {
    const wrappedClient: any = {};
    for (const key of serviceClientMethodKeys)
        wrappedClient[key] = (...params: any[]) => Promise.resolve<any>(deferredServiceClient).then(serviceClient => serviceClient[key](...params));

    return wrappedClient;
}

export async function createServiceClient(url: string) {
    const catalog = new ConfigurationModels.CatalogServiceClient(new Axon.HttpClientTransport(url), new Axon.EntanglementProtocol(), 'nucleus.configuration.models.catalog');
    const dataAccess = new ConfigurationModels.DataAccessServiceClient(new Axon.HttpClientTransport(url), new Axon.EntanglementProtocol(), 'nucleus.configuration.models.dataaccess');
    const coreProvider = new ConfigurationCore.ProviderServiceClient(new Axon.HttpClientTransport(url), new Axon.EntanglementProtocol(), 'nucleus.configuration.core.provider');
    const coreLifecycle = new ConfigurationCore.LifecycleServiceClient(new Axon.HttpClientTransport(url), new Axon.EntanglementProtocol(), 'nucleus.configuration.core.lifecycle');
    const layoutProvider = new ConfigurationLayout.ProviderServiceClient(new Axon.HttpClientTransport(url), new Axon.EntanglementProtocol(), 'nucleus.configuration.layout.provider');
    const louverSelector = new LouverSelection.ConfigurationServiceClient(new Axon.HttpClientTransport(url), new Axon.EntanglementProtocol(), 'nucleus.louverselection.configuration');
    const projectionProvider = new ConfigurationProjection.ProviderServiceClient(new Axon.HttpClientTransport(url), new Axon.EntanglementProtocol(), 'nucleus.configuration.projection.provider');

    return new ServiceClient(url, catalog, dataAccess, coreProvider, coreLifecycle, layoutProvider, louverSelector, projectionProvider);
}

export function createWellspringServiceClient<T extends Wellspring.IServiceClient>(Client: new (transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) => T, url: string, identifier?: string) {
    const transport = new Axon.HttpClientTransport(url);
    const protocol = new Axon.EntanglementProtocol();

    const client = new Client(transport, protocol, identifier);

    return client;
}